import React, { useState } from "react";
import FormInput from "./common/FormInput.tsx";
import FormTextArea from "./common/FormTextArea.tsx";
import FormButton from "./common/FormButton.tsx";
import Modal from "./common/Modal.tsx";
import useModal from "../utils/useModal.ts";

type FormData = {
  name: string;
  organization: string;
  email: string;
  phone: string;
  message: string;
};

type FormErrors = {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
};

export default function ContactForm() {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    organization: "",
    email: "",
    phone: "",
    message: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const { isOpen, message, isSuccess, openModal, closeModal } = useModal();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" })); // Clear error when typing
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required.";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email address.";
    }
    if (formData.phone && !/^\d+$/.test(formData.phone)) {
      newErrors.phone = "Phone number must be numeric.";
    }
    if (!formData.message.trim()) newErrors.message = "Message is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateForm()) return;

    try {
      const response = await fetch("https://formspree.io/f/mwpvbeyw", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        openModal("Message sent successfully!", true);
        setFormData({ name: "", organization: "", email: "", phone: "", message: "" });
      } else {
        openModal("Error sending message. Please try again.", false);
      }
    } catch (error) {
      openModal("An unexpected error occurred.", false);
    }
  };

  return (
    <section id="contact" className="bg-primary py-16 px-4 sm:px-8 md:px-16 lg:px-32 flex justify-center items-center">
      <div className="w-full max-w-[800px] p-6 sm:p-10 bg-white shadow-lg rounded-lg flex flex-col items-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-primary text-center mb-6">Contact Us</h2>
        <form onSubmit={handleSubmit} className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput label="Name" name="name" value={formData.name} onChange={handleChange} error={errors.name} required />
          <FormInput label="Organization" name="organization" value={formData.organization} onChange={handleChange} />
          <FormInput label="Email" name="email" type="email" value={formData.email} onChange={handleChange} error={errors.email} required />
          <FormInput label="Phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} error={errors.phone} />
          <FormTextArea label="How Can We Help?" name="message" value={formData.message} onChange={handleChange} error={errors.message} required />
          <div className="col-span-1 md:col-span-2 flex justify-center">
            <FormButton label="Submit" />
          </div>
        </form>
      </div>
      <Modal isOpen={isOpen} message={message} isSuccess={isSuccess} onClose={closeModal} />
    </section>
  );
}
